/**
 * HubSpot OAuth Utilities (Story 25.1, AC #1, #2, #3, #8)
 *
 * HubSpot uses traditional Authorization Code flow with client_secret (confidential client).
 * NO PKCE — unlike Klaviyo/Meta/Google.
 * Token exchange is form-encoded (application/x-www-form-urlencoded), NOT JSON.
 *
 * Uses v3 OAuth endpoints (v1 deprecated, sunset expected 2026):
 * - Authorization: https://app.hubspot.com/oauth/authorize
 * - Token: POST https://api.hubapi.com/oauth/v3/token
 * - Token Introspection: POST https://api.hubapi.com/oauth/v3/introspect
 *
 * @see https://developers.hubspot.com/changelog/new-oauth-v3-api-endpoints-and-standardized-error-responses
 */

import { safeFetch } from "../safe-fetch";

const HUBSPOT_AUTH_BASE = "https://app.hubspot.com/oauth/authorize";
const HUBSPOT_TOKEN_URL = "https://api.hubapi.com/oauth/v3/token";
const HUBSPOT_INTROSPECT_URL = "https://api.hubapi.com/oauth/v3/introspect";

/**
 * Required OAuth scopes for Story 25.1 (CRM core).
 *
 * NOTE: `content` scope intentionally excluded from required scopes because it
 * requires SuperAdmin role — non-SuperAdmin users cannot grant it, which would
 * block the entire OAuth flow. Instead, `content` is listed in HUBSPOT_OPTIONAL_SCOPES
 * and requested separately via the `optional_scope` parameter.
 *
 * @see https://community.hubspot.com/t5/APIs-Integrations/Unable-to-complete-OAuth-with-scope-content-unless-account-has/m-p/277527
 */
export const HUBSPOT_SCOPES = [
  "crm.objects.contacts.read",
  "crm.objects.deals.read",
  "crm.objects.companies.read",
  "crm.schemas.contacts.read",
  "crm.schemas.deals.read",
  "crm.schemas.companies.read",
  "crm.objects.owners.read",
  "oauth",
  "e-commerce",
  // Story 25.9: Tickets + engagements. Ticket scope needs "Edit Property Settings"
  // for non-SuperAdmins — we request it as REQUIRED. Pre-25.9 connections that
  // never granted it will hit 403 on ticket/engagement endpoints; the adapter
  // degrades gracefully (skip + flag) rather than fail the whole fetch.
  "tickets",
] as const;

/**
 * Optional scopes — requested but user can still connect if they can't grant these.
 * `content` requires SuperAdmin role in HubSpot.
 *
 * Story 25.10:
 *   `forms` covers Forms v3 list + form-integrations v1 submissions list.
 *   `crm.objects.marketing_events.read` covers /marketing/v3/marketing-events
 *   (non-SuperAdmin accounts may still be unable to grant these → adapter
 *   degrades the form/event summary fields to zero + flags availability).
 */
export const HUBSPOT_OPTIONAL_SCOPES = [
  "content",
  "forms",
  "crm.objects.marketing_events.read",
  // Story 25.11: Workflows (Automation v4 BETA) + Custom Behavioral Events.
  // Both are optional — most portals do not have them enabled, and they are
  // not required for CRM core metrics. Adapter degrades gracefully via
  // scope_missing → zeros + flag when absent.
  "automation",
  "behavioral_events.event_definitions.read_write",
] as const;

export interface HubSpotTokenResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly expires_in: number;
  readonly token_type: string;
}

export interface HubSpotTokenInfo {
  /** Whether the token is currently active (v3 introspect RFC 7662) */
  readonly active?: boolean;
  readonly hub_id: number;
  readonly hub_domain: string;
  readonly user_id: number;
  readonly user: string;
  readonly scopes: string[];
  readonly token_type: string;
  readonly app_id: number;
}

export interface HubSpotCallbackError {
  readonly error: string;
  readonly message: string;
}

/**
 * Build HubSpot OAuth authorization URL.
 * No PKCE — confidential client flow with client_secret.
 */
export function buildHubSpotAuthUrl(options: {
  clientId: string;
  redirectUri: string;
  stateKey: string;
}): string {
  const params = new URLSearchParams({
    client_id: options.clientId,
    redirect_uri: options.redirectUri,
    scope: HUBSPOT_SCOPES.join(" "),
    optional_scope: HUBSPOT_OPTIONAL_SCOPES.join(" "),
    state: options.stateKey,
  });
  return `${HUBSPOT_AUTH_BASE}?${params.toString()}`;
}

/**
 * Exchange authorization code for access + refresh tokens.
 * Form-encoded POST (NOT JSON) — HubSpot requirement.
 */
export async function exchangeHubSpotCode(
  code: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string,
): Promise<HubSpotTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    client_secret: clientSecret,
    redirect_uri: redirectUri,
    code,
  });

  const result = await safeFetch(HUBSPOT_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!result.success) {
    throw new Error(
      `HubSpot token exchange failed: ${result.error.message}`,
    );
  }

  try {
    return (await result.data.json()) as HubSpotTokenResponse;
  } catch {
    throw new Error("HubSpot token exchange failed: invalid JSON response");
  }
}

/**
 * Refresh access token using refresh_token.
 * HubSpot access tokens expire in 30 minutes (1800s).
 * Refresh tokens never expire (only invalidated on app uninstall).
 */
export async function refreshHubSpotToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string,
): Promise<HubSpotTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: clientId,
    client_secret: clientSecret,
    refresh_token: refreshToken,
  });

  const result = await safeFetch(HUBSPOT_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!result.success) {
    throw new Error(
      `HubSpot token refresh failed: ${result.error.message}`,
    );
  }

  try {
    return (await result.data.json()) as HubSpotTokenResponse;
  } catch {
    throw new Error("HubSpot token refresh failed: invalid JSON response");
  }
}

/**
 * Verify access token and retrieve portal info via v3 introspect.
 * POST /oauth/v3/introspect — replaces deprecated GET /oauth/v1/access-tokens/{token}
 * Returns hub_id, user, scopes, hub_domain.
 *
 * @see https://developers.hubspot.com/changelog/new-oauth-v3-api-endpoints-and-standardized-error-responses
 */
export async function verifyHubSpotAccessToken(
  accessToken: string,
): Promise<HubSpotTokenInfo> {
  const body = new URLSearchParams({ token: accessToken });

  const result = await safeFetch(HUBSPOT_INTROSPECT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!result.success) {
    throw new Error(
      `HubSpot token verification failed: ${result.error.message}`,
    );
  }

  let tokenInfo: HubSpotTokenInfo;
  try {
    tokenInfo = (await result.data.json()) as HubSpotTokenInfo;
  } catch {
    throw new Error("HubSpot token verification failed: invalid JSON response from introspect endpoint");
  }

  // R2-H1: v3 introspect returns HTTP 200 with active:false for expired/revoked tokens (RFC 7662)
  if (tokenInfo.active === false) {
    throw new Error("HubSpot token is expired or revoked (introspect active=false)");
  }

  return tokenInfo;
}

/**
 * Parse OAuth callback errors from URL search params.
 * HubSpot returns error info as query params on the redirect URI.
 */
export function parseHubSpotCallbackError(
  searchParams: URLSearchParams,
): HubSpotCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  const description =
    searchParams.get("error_description") || "OAuth authorization failed";

  return { error, message: description };
}
